/* Which brokers this book can be connected to, and what each one needs told.
 *
 * This used to be one broker written straight into the screen: two labelled
 * inputs, one button, one set of hints, all of it naming Interactive Brokers.
 * A second broker would have meant a second copy of the whole thing, and the
 * screen would have grown a branch for every broker after that.
 *
 * So a connection is a record here rather than markup there. The fields listed
 * on a broker are what the form draws, in order; picking a different broker
 * draws a different form, and the screen never learns which broker it is
 * drawing. Adding a third is an entry in this list.
 *
 * ── on §1, which rules out broker credentials ─────────────────────────────
 *
 * The ban is on keys that can act: account passwords, trading keys, OAuth into
 * a brokerage. Every key named here is read-only and that is the whole of the
 * argument — a Flex token fetches a statement, a MEXC key marked read-only
 * returns balances and open positions. Neither can place an order, neither can
 * move money out, and the worst either can do in the wrong hands is repeat what
 * this book already holds. A key that could trade would not be in this file,
 * and `holds` below is not a permission — nothing here ever writes to a broker.
 *
 * Where they live matters as much as what they are. Credentials are kept in
 * settings, which sit outside BOOK_STORES, so they are in neither the JSON
 * export nor the sync blob. They stay on the device they were typed into and
 * travel only to the broker they belong to — never to our Worker. */

import type { AccountRecord } from '../types';

export interface BrokerField {
  key: string;
  label: string;
  /** Masked on screen, behind the reveal toggle. */
  secret?: boolean;
  mono?: boolean;
  numeric?: boolean;
  hint?: string;
}

export interface BrokerDef {
  id: string;
  name: string;
  /** What pressing the button reads, in one sentence, shown above the fields. */
  reads: string;
  /** Where the key is made. Shown under them. */
  where: string;
  fields: BrokerField[];
  /** Which of the book's accounts this can be pointed at. A Flex statement is
      share trades and belongs in a share account; MEXC's answer is contracts
      and belongs in the futures one. */
  holds: 'shares' | 'futures';
  /** The button. */
  action: string;
  /** The one host it talks to. The native shells open a door to exactly these
      and to nothing else — see `lib/brokerLink.ts` and `src-tauri/src/main.rs`. */
  host: string;
  /** Other hosts of the same broker, where one API is split across two. MEXC's
      spot wallet answers on a different host from its futures one. */
  extraHosts?: readonly string[];
}

const IBKR: BrokerDef = {
  id: 'ibkr',
  name: 'Interactive Brokers',
  reads: 'Runs a saved Flex query and brings the share trades in it into the book.',
  where: 'Account Management → Reporting → Flex Queries: create a token, and a saved query covering your trades.',
  holds: 'shares',
  action: 'Pull my trades',
  host: 'ndcdyn.interactivebrokers.com',
  fields: [
    {
      key: 'token', label: 'Flex token', secret: true, mono: true,
      hint: 'A report key. It cannot place an order or move money, which is the only reason this app holds one.',
    },
    {
      key: 'queryId', label: 'Query id', mono: true, numeric: true,
      hint: 'A short number the saved query is listed under, like 1234567, not the token. They are easy to paste the wrong way round, and the service answers a swapped pair the same way it answers a wrong one.',
    },
  ],
};

const MEXC: BrokerDef = {
  id: 'mexc',
  name: 'MEXC futures',
  reads: 'Reads the futures wallet and every open contract, so the balance and the positions stop being typed in by hand.',
  where: 'API Management → create a key with reading enabled and trading left off. Leave the IP field empty; the key then expires after 90 days and is renewed each quarter.',
  holds: 'futures',
  action: 'Read my MEXC account',
  host: 'contract.mexc.com',
  extraHosts: ['api.mexc.com'],
  fields: [
    {
      key: 'apiKey', label: 'Access key', mono: true,
      hint: 'The read-only half of the pair. Give the key reading rights only. This app never sends an order, and a key that could trade does not belong on a phone.',
    },
    {
      key: 'secret', label: 'Secret key', secret: true, mono: true,
      hint: 'Signs each request on this device. It is never transmitted. Only the signature it produces is.',
    },
  ],
};

export const BROKERS: readonly BrokerDef[] = [IBKR, MEXC];

/** Every host a connection is allowed to reach. The native shells check against
    this same list, so a broker added above cannot be reached until it is — and
    a typo in a URL cannot turn the native fetch into a general-purpose one. */
export const BROKER_HOSTS: readonly string[] = BROKERS.flatMap((b) => [b.host, ...(b.extraHosts ?? [])]);

export function brokerById(id: string | undefined): BrokerDef | undefined {
  return BROKERS.find((b) => b.id === id);
}

/** The accounts a given connection can be pointed at. A book with no futures
    account cannot receive MEXC's answer, and the screen says so rather than
    offering an empty select. */
export function accountsFor(def: BrokerDef, accounts: AccountRecord[]): AccountRecord[] {
  return accounts.filter((a) => (def.holds === 'futures' ? a.isFutures === true : a.isFutures !== true));
}

/* What has been typed in for one broker.
 *
 * Read through here rather than off settings directly, because the shape
 * changed: there used to be three flat keys, all of them named after the one
 * broker that existed. Those are still read and migrated on the way past —
 * the same trick `benchmarksFrom` uses — so a device that had a Flex token
 * saved still has it, and nothing writes the old keys again. */
export interface BrokerSettings {
  brokerFields?: Record<string, Record<string, string>>;
  broker?: string;
  ibkrFlexToken?: string;
  ibkrFlexQueryId?: string;
  ibkrAccount?: string;
}

export function connectionFor(settings: BrokerSettings, brokerId: string): Record<string, string> {
  const saved = settings.brokerFields?.[brokerId];
  if (saved) return saved;
  if (brokerId === 'ibkr') {
    const legacy: Record<string, string> = {};
    if (settings.ibkrFlexToken) legacy['token'] = settings.ibkrFlexToken;
    if (settings.ibkrFlexQueryId) legacy['queryId'] = settings.ibkrFlexQueryId;
    return legacy;
  }
  return {};
}

/** Which account this connection files into, falling back to the first one it
    is allowed to use. */
export function accountFor(
  settings: BrokerSettings & { brokerAccounts?: Record<string, string> },
  def: BrokerDef,
  accounts: AccountRecord[],
): string {
  const allowed = accountsFor(def, accounts);
  const chosen = settings.brokerAccounts?.[def.id]
    ?? (def.id === 'ibkr' ? settings.ibkrAccount : undefined);
  if (chosen && allowed.some((a) => a.id === chosen)) return chosen;
  return allowed[0]?.id ?? '';
}

/** Whether every field has something in it. Nothing is guessed or defaulted:
    a half-filled form is refused with a sentence rather than sent and failed. */
export function missingFields(def: BrokerDef, values: Record<string, string>): BrokerField[] {
  return def.fields.filter((f) => (values[f.key] ?? '').trim() === '');
}

/** Which broker's form is open. Falls back to the first one rather than to
    nothing, so the section is never blank on a device that has chosen none. */
export function chosenBroker(settings: BrokerSettings): BrokerDef {
  return brokerById(settings.broker) ?? BROKERS[0]!;
}
